import { useEffect, useState } from "react";
import { Banner } from "../common/Banner";
import Swal from "sweetalert2";
import SocioSearch from "../common/SocioSearch";
import { changeRut } from "../../services/companiesService";

export default function ChangeRut({ isSubmitting, onReload }) {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newRut, setNewRut] = useState("");
  const [rutError, setRutError] = useState("");

  // Función para formatear el RUT automáticamente
  const formatRut = (value) => {
    // Remover todo lo que no sea número
    const numbers = value.replace(/\D/g, "");
    
    // Limitar a 9 dígitos máximo (8 + 1 dígito verificador)
    if (numbers.length > 9) return newRut;
    
    // Si tiene menos de 2 dígitos, devolver sin formato
    if (numbers.length < 2) return numbers;
    
    // Separar el dígito verificador
    const body = numbers.slice(0, -1);
    const dv = numbers.slice(-1);

    return `${body}-${dv}`;
  };

  // Función para validar el RUT
  const validateRut = (rut) => {
    // Remover puntos y guión
    const cleanRut = rut.replace(/\./g, "").replace("-", "");
    
    // Verificar que tenga al menos 8 dígitos
    if (cleanRut.length < 8) {
      return "El RUT debe tener al menos 8 dígitos";
    }
    
    // Verificar que tenga máximo 9 dígitos
    if (cleanRut.length > 9) {
      return "El RUT no puede tener más de 9 dígitos";
    }
    
    // Verificar que solo contenga números
    if (!/^\d+$/.test(cleanRut)) {
      return "El RUT solo debe contener números";
    }
    
    return "";
  };

  // Manejar cambio en el input del RUT
  const handleRutChange = (e) => {
    const value = e.target.value;
    const formatted = formatRut(value);
    setNewRut(formatted);
    
    // Validar el RUT
    const error = validateRut(formatted);
  };

  // Verificar si el formulario es válido
  const isValid = selectedCompany && newRut && !rutError && newRut.length >= 10; // Mínimo 8 dígitos + puntos + guión

  // Función para manejar el cambio de RUT
  const handleChangeRut = async () => {
    if (!isValid) return;

    const confirmation = await Swal.fire({
      title: "¿Estás seguro?",
      html: `
        <div class="text-left">
          <p class="mb-2"><strong>Empresa:</strong> ${selectedCompany.business_name}</p>
          <p class="mb-2"><strong>RUT actual:</strong> ${selectedCompany.rut}</p>
          <p class="mb-2"><strong>Nuevo RUT:</strong> ${newRut}</p>
          <p class="text-red-600 text-sm mt-3">Esta acción no se puede deshacer.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cambiar RUT",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0488AF",
      cancelButtonColor: "#DC3465"
    });

    if (!confirmation.isConfirmed) return;

    try{
        isSubmitting(true);
        const response = await changeRut(selectedCompany.rut, newRut);
        isSubmitting(false);
        if (response.success) {
          Swal.fire({
            title: "RUT cambiado exitosamente",
            text: `El RUT de la empresa ${selectedCompany.business_name} ha sido cambiado a ${newRut}.`,
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#0488AF"
          });
          onReload();
        }
        else {
          Swal.fire({
            title: "Error al cambiar el RUT",
            text: response.message || "Por favor, intenta nuevamente.",
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#DC3465"
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error al cambiar el RUT",
          text: error.message || "Ocurrió un error al intentar cambiar el RUT. Por favor, intenta nuevamente.",
          icon: "error",
          confirmButtonText: "Aceptar",
          confirmButtonColor: "#DC3465"
        });
        isSubmitting(false);
      }
    }

  // Función para limpiar la selección
  const clearSelection = () => {
    setSelectedCompany(null);
    setNewRut("");
    setRutError("");
  };

  // Renderizar la tarjeta de la empresa seleccionada
  const renderCompanyCard = (company) => {
    if (!company) {
      return (
        <div className="h-48 mt-4 p-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500 text-center">
            Selecciona una empresa para ver sus detalles
          </p>
        </div>
      );
    }

    return (
      <div className="h-48 mt-4 p-4 rounded-lg shadow-lg transition-all duration-200 bg-gradient-to-br from-[#E6F7FB] to-[#F0FAFE] border-2 border-[#0488AF] hover:shadow-xl hover:shadow-[#0488AF]/20">
        <div className="h-full flex flex-col">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-2">{company.business_name}</h3>
            <p className="text-sm font-semibold text-gray-700 mb-3 line-clamp-1">{company.trade_name}</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-600">RUT actual:</span>
                <span className="text-gray-900 font-mono text-sm bg-yellow-100 px-2 py-1 rounded">{company.rut}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-600">Estado:</span>
                <span className={`font-bold px-2 py-1 rounded-full text-xs ${
                  company.status === 'Active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {company.status}
                </span>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-2 mt-2">
            <div className="text-xs text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Email:</span>
                <span className="truncate ml-2 max-w-32">{company.contact_email}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Banner
        title="Cambio de RUT"
        message="Selecciona una empresa y asigna un nuevo RUT."
      />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
        <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
          <span className="bg-[#0488AF] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">1</span>
          Seleccionar Empresa
        </h2>
        
        <SocioSearch onSocioSelect={setSelectedCompany} />
        
        {selectedCompany && (
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-green-600 font-medium">
              ✓ Empresa seleccionada: {selectedCompany.business_name}
            </span>
            <button
              onClick={clearSelection}
              className="text-red-600 hover:text-red-800 text-sm underline"
            >
              Limpiar selección
            </button>
          </div>
        )}

        {renderCompanyCard(selectedCompany)}
      </div>

      {selectedCompany && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
            <span className="bg-[#0488AF] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">2</span>
            Nuevo RUT
          </h2>
          
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ingresa el nuevo RUT
            </label>
            <input
              type="text"
              value={newRut}
              onChange={handleRutChange}
              placeholder="12345678-9"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent font-mono text-lg ${
                rutError 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-[#0488AF]'
              }`}
            />
            {rutError && (
              <p className="mt-2 text-sm text-red-600">{rutError}</p>
            )}
            {newRut && !rutError && (
              <p className="mt-2 text-sm text-green-600">✓ Formato válido</p>
            )}
          </div>

          {selectedCompany && newRut && (
            <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">Resumen del cambio</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p><strong>Empresa:</strong> {selectedCompany.business_name}</p>
                <p><strong>RUT actual:</strong> {selectedCompany.rut}</p>
                <p><strong>Nuevo RUT:</strong> {newRut}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mensaje de error si no es válido */}
      {selectedCompany && newRut && !isValid && (
        <div className="mt-6 max-w-2xl mx-auto">
          <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-semibold text-red-800">Error de validación</h4>
                <p className="text-sm text-red-700 mt-1">
                  {rutError || "Por favor corrige los errores antes de continuar"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botón de cambio */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={handleChangeRut}
          disabled={!isValid}
          className={`px-8 py-3 text-white font-bold rounded-lg transition-all duration-200 transform ${
            isValid
              ? "bg-gradient-to-r from-[#0488AF] to-[#0369A1] hover:from-[#0369A1] hover:to-[#0284C7] hover:scale-105 shadow-lg hover:shadow-xl"
              : "bg-gray-400 cursor-not-allowed opacity-60"
          }`}
        >
          {isValid ? "🔄 Cambiar RUT" : "Cambiar RUT"}
        </button>
      </div>
    </div>
  );
}